import { Col, Row } from "antd";
import React, { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { InputField, ReactSelectCreateAble, ReactSelectField } from "../../../../../atoms/FormElement";
import {
  getEmailValidationRules,
  getInputPlaceholderMessage,
  getMinMaxValidationRules,
  getSelectPlaceholderMessage,
  getValidationProps,
} from "../../../../../libs/form";
import { filterDropdownValue } from "../../utils";

export const CompanyTenantDetailsFields = () => {
  const { configData, bookingForm } = useSelector((state: any) => state.newBooking);
  const { control, setValue, formState } = useFormContext();

  const { errors } = formState;

  const defaultProps = { control, setValue };
  const companyName = useWatch({ control, name: "commercial.company.name" });
  const companyTenants = bookingForm?.companyTenants || [];

  const companyTenantOptions = useMemo(
    () =>
      companyTenants.map((item) => ({
        value: item?.companyTenantId,
        label: item?.name,
      })),
    [companyTenants],
  );

  const companyNameInitialValue = useMemo(() => {
    if (!companyName) return undefined;

    if (typeof companyName === "object" && companyName !== null) {
      const label = String(companyName?.label || "");
      if (!label) return undefined;
      return {
        value: companyName?.value ?? null,
        label,
      };
    }

    const label = String(companyName);
    return companyTenantOptions.find((option) => option.label === label) || { value: null, label };
  }, [companyName, companyTenantOptions]);

  const optionsWithInitialValue = useMemo(() => {
    if (!companyNameInitialValue) return companyTenantOptions;

    const hasInitialInOptions = companyTenantOptions.some(
      (option) => option.label === companyNameInitialValue.label && option.value === companyNameInitialValue.value,
    );

    return hasInitialInOptions ? companyTenantOptions : [companyNameInitialValue, ...companyTenantOptions];
  }, [companyTenantOptions, companyNameInitialValue]);

  const handleCompanyNameChange = (option) => {
    setValue("commercial.company.name", option || null, { shouldDirty: true, shouldValidate: true });

    const selectedCompanyTenant = companyTenants.find((item) => item?.companyTenantId === option?.value);
    const selectedCountry = selectedCompanyTenant?.country
      ? filterDropdownValue(selectedCompanyTenant?.country, configData?.nationalityList)
      : null;

    setValue("commercial.company.address", selectedCompanyTenant?.address || "");
    setValue("commercial.company.city", selectedCompanyTenant?.city || "");
    setValue("commercial.company.workPhone", selectedCompanyTenant?.workPhone || "");
    setValue("commercial.company.phone", selectedCompanyTenant?.companyPhone || selectedCompanyTenant?.workPhone || "");
    setValue("commercial.company.poBox", selectedCompanyTenant?.poBox || "");
    setValue("commercial.company.licenseNo", selectedCompanyTenant?.tradeLicense || "");
    setValue("commercial.company.country", selectedCountry || null);
  };

  const handleCompanyNameCreate = (inputValue: string) => {
    const createdOption = inputValue ? { value: null, label: inputValue } : null;
    setValue("commercial.company.name", createdOption, { shouldDirty: true, shouldValidate: true });
    setValue("commercial.company.address", "");
    setValue("commercial.company.city", "");
    setValue("commercial.company.workPhone", "");
    setValue("commercial.company.phone", "");
    setValue("commercial.company.poBox", "");
    setValue("commercial.company.licenseNo", "");
    setValue("commercial.company.country", null);
  };

  return (
    <>
      <Row gutter={24}>
        <Col span={8}>
          <InputField
            fieldname="commercial.company.email"
            label="Email address"
            {...defaultProps}
            {...getValidationProps({
              fieldName: "commercial.company.email",
              errors,
              isRequired: true,
              rules: getEmailValidationRules(),
            })}
            iProps={{ placeholder: getInputPlaceholderMessage("email address") }}
          />
        </Col>
        <Col span={8}>
          <ReactSelectCreateAble
            fieldname="commercial.company.name"
            label="Company name"
            control={control}
            setValue={setValue}
            {...getValidationProps({ fieldName: "commercial.company.name", errors, isRequired: true })}
            options={optionsWithInitialValue}
            initalValue={companyNameInitialValue}
            onChange={handleCompanyNameChange}
            onCreateOption={handleCompanyNameCreate}
            iProps={{ placeholder: getInputPlaceholderMessage("company name") }}
          />
        </Col>
        <Col span={8}>
          <InputField
            fieldname="commercial.company.phone"
            label="Phone number"
            {...defaultProps}
            {...getValidationProps({
              fieldName: "commercial.company.phone",
              errors,
              isRequired: true,
              rules: getMinMaxValidationRules({ label: "Phone number", min: 8, max: 9 }),
            })}
            iProps={{ addonBefore: "+971", placeholder: "123456789" }}
          />
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={8}>
          <InputField
            isRequired
            fieldname="commercial.company.address"
            label="Office address"
            {...defaultProps}
            {...getValidationProps({ fieldName: "commercial.company.address", errors, isRequired: true })}
            iProps={{ placeholder: getInputPlaceholderMessage("office address") }}
          />
        </Col>
        <Col span={8}>
          <InputField
            fieldname="commercial.company.city"
            label="City"
            {...defaultProps}
            {...getValidationProps({ fieldName: "commercial.company.city", errors, isRequired: true })}
            iProps={{ placeholder: getInputPlaceholderMessage("city") }}
          />
        </Col>
        <Col span={8}>
          <ReactSelectField
            fieldname="commercial.company.country"
            label="Country"
            selectOption={configData.nationalityList}
            {...defaultProps}
            {...getValidationProps({ fieldName: "commercial.company.country", errors, isRequired: true })}
            iProps={{ placeholder: getSelectPlaceholderMessage("country") }}
          />
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={8}>
          <InputField
            fieldname="commercial.company.poBox"
            label="PO box"
            {...defaultProps}
            {...getValidationProps({ fieldName: "commercial.company.poBox", errors, isRequired: false })}
            iProps={{ placeholder: getInputPlaceholderMessage("po box") }}
          />
        </Col>
        <Col span={8}>
          <InputField
            isRequired
            fieldname="commercial.company.licenseNo"
            label="License no."
            {...defaultProps}
            {...getValidationProps({ fieldName: "commercial.company.licenseNo", errors, isRequired: true })}
            iProps={{ placeholder: getInputPlaceholderMessage("license no.") }}
          />
        </Col>
        <Col span={8}>
          <InputField
            fieldname="commercial.company.workPhone"
            label="Work phone"
            {...defaultProps}
            {...getValidationProps({
              fieldName: "commercial.company.workPhone",
              errors,
              isRequired: false,
              rules: getMinMaxValidationRules({ label: "Work phone", min: 8, max: 12 }),
            })}
            iProps={{ placeholder: "12345678" }}
          />
        </Col>
      </Row>
    </>
  );
};
